import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ParametrosService } from '../../../services/parametros.service';
import { SweetAlertService } from '../../../services/sweet-alert.service';
import { UserService } from '../../../services/user.service';
import { PERFIL_COPY } from '../../data/perfil-microempresario.content';
import { segmentoToTipoPersonaApi } from '../documentos/perfil-documentos.util';
import { isPerfilJuridica } from '../perfil-tipo.util';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import {
  parseMontoDigits,
  parsePlazoNumber,
  resolveSegmento,
  resolveTipoLinea,
} from '../segmento/perfil-segmento.util';
import { PerfilSolicitudService } from '../solicitud/perfil-solicitud.service';
import {
  SolicitaCreditoDestino,
  SolicitaCreditoTipoId,
} from './solicita-credito.destinos';

interface DestinoCreditoApi {
  idDestinoCredito?: number;
  codigo?: string;
  nombre?: string;
  tooltip?: string;
  orden?: number;
  /** API puede mandar boolean o 0/1. */
  esOtro?: boolean | number | string;
}

/**
 * Pantalla inicial de solicitud perfil v2 — monto / plazo / tipo / destino.
 */
@Component({
  selector: 'app-perfil-solicita-credito',
  templateUrl: './perfil-solicita-credito.component.html',
  styleUrls: ['./perfil-solicita-credito.component.scss'],
  standalone: false,
})
export class PerfilSolicitaCreditoComponent implements OnInit, OnDestroy {
  @ViewChild('montoTooltip')
  private montoTooltip?: { close(): void };
  @ViewChild('plazoTooltip')
  private plazoTooltip?: { close(): void };

  readonly copy = PERFIL_COPY.solicitaCredito;
  readonly botones = PERFIL_COPY.botones;
  destinos: SolicitaCreditoDestino[] = [];
  readonly tipos: ReadonlyArray<{
    id: SolicitaCreditoTipoId;
    titulo: string;
    desc: string;
  }> = [
    {
      id: 'bancoldex',
      titulo: this.copy.tipos.bancoldex.titulo,
      desc: this.copy.tipos.bancoldex.desc,
    },
    {
      id: 'aliados',
      titulo: this.copy.tipos.aliados.titulo,
      desc: this.copy.tipos.aliados.desc,
    },
    {
      id: 'ambas',
      titulo: this.copy.tipos.ambas.titulo,
      desc: this.copy.tipos.ambas.desc,
    },
  ];

  readonly otroTextoMaxLength = 500;
  form: UntypedFormGroup;
  montoErrorMessage = '';
  plazoErrorMessage = '';
  mensajeMontoExpress = '';
  mensajePlazoExpress = '';
  mostrarAyudaExpress = false;
  private sub = new Subscription();
  private syncingTipos = false;
  private paramsReady = false;
  private destinosReady = false;
  private saving = false;
  /** Caché PN: precargados al inicio; el filtro por segmento es solo en cliente. */
  private destinosExpress: SolicitaCreditoDestino[] = [];
  private destinosNatural: SolicitaCreditoDestino[] = [];
  /** Intersección express ∩ natural (aplican a ambos tipos). */
  private destinosComunes: SolicitaCreditoDestino[] = [];
  /** Último catálogo aplicado a la UI (evita rearmar el form sin cambio). */
  private lastTipoDestinos: 'express' | 'natural' | 'comunes' | 'juridica' | null =
    null;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly parametrosService: ParametrosService,
    private readonly solicitudService: PerfilSolicitudService,
    private readonly sweetAlert: SweetAlertService,
    private readonly userService: UserService
  ) {
    this.form = this.fb.group(
      {
        monto: ['', Validators.required],
        plazo: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
        tipoCredito: this.fb.group(
          {
            bancoldex: [false],
            aliados: [false],
            ambas: [false],
          },
          { validators: [tipoCreditoRequeridoValidator] }
        ),
        destinos: this.fb.group({}),
        otroTexto: [''],
      },
      { validators: [destinosRequeridosValidator] }
    );
  }

  ngOnInit(): void {
    // Ruta solo para alta (home ya validó restricción; aquí se limpia id previo).
    this.solicitudService.prepararNueva();
    this.mostrarAyudaExpress = !isPerfilJuridica(
      this.userService.getUser()?.tipoPersona
    );

    this.sub.add(
      this.form.get('destinos')!.valueChanges.subscribe(() => {
        this.syncOtroTextoValidators();
        this.form.updateValueAndValidity({ emitEvent: false });
      })
    );

    this.sub.add(
      this.segmentoService.ensureParamsLoaded().subscribe((params) => {
        this.paramsReady = true;
        if (this.mostrarAyudaExpress) {
          this.mensajeMontoExpress = params.mensajeMonto ?? '';
          this.mensajePlazoExpress = params.mensajePlazo ?? '';
        }
        this.validarMonto();
        this.validarPlazo();
        this.aplicarDestinosPorSegmento();
      })
    );

    this.precargarDestinos();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  cerrarAyudasExpress(): void {
    this.montoTooltip?.close();
    this.plazoTooltip?.close();
  }

  get destinosCol1(): SolicitaCreditoDestino[] {
    return this.destinos.filter((d) => d.columna === 1);
  }

  get destinosCol2(): SolicitaCreditoDestino[] {
    return this.destinos.filter((d) => d.columna === 2);
  }

  get continuarDisabled(): boolean {
    return (
      this.form.invalid ||
      !this.paramsReady ||
      !this.destinosReady ||
      this.saving
    );
  }

  /** Input de texto libre si marcó alguna opción con ES_OTRO. */
  get showOtroInput(): boolean {
    return this.destinosEsOtroSeleccionados.length > 0;
  }

  private get destinosEsOtroSeleccionados(): SolicitaCreditoDestino[] {
    const values = this.form.get('destinos')?.value as
      | Record<string, boolean>
      | undefined;
    if (!values) {
      return [];
    }
    return this.destinos.filter((d) => d.esOtro && values[d.id]);
  }

  private syncOtroTextoValidators(): void {
    const ctrl = this.form.get('otroTexto')!;
    if (this.showOtroInput) {
      ctrl.setValidators([
        Validators.required,
        Validators.maxLength(this.otroTextoMaxLength),
      ]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  errorMessage(controlName: 'monto' | 'plazo'): string {
    if (controlName === 'monto' && this.montoErrorMessage) {
      return this.montoErrorMessage;
    }
    if (controlName === 'plazo' && this.plazoErrorMessage) {
      return this.plazoErrorMessage;
    }
    return this.copy.validaciones.campoObligatorio;
  }

  /**
   * Reglas tipo de línea:
   * - Se puede desmarcar cualquier opción.
   * - Marcar "Ambas" → marca las 3.
   * - Desmarcar "Ambas" → desmarca las 3.
   * - Marcar Bancóldex + Aliados → marca también "Ambas".
   * - Desmarcar una individual → desmarca "Ambas".
   */
  onTipoToggle(id: SolicitaCreditoTipoId): void {
    if (this.syncingTipos) {
      return;
    }
    this.syncingTipos = true;

    const group = this.form.get('tipoCredito') as UntypedFormGroup;
    const checked = !!group.get(id)?.value;

    if (id === 'ambas') {
      group.patchValue(
        {
          bancoldex: checked,
          aliados: checked,
          ambas: checked,
        },
        { emitEvent: false }
      );
    } else {
      const bancoldex = !!group.get('bancoldex')?.value;
      const aliados = !!group.get('aliados')?.value;
      group.patchValue(
        {
          bancoldex,
          aliados,
          ambas: bancoldex && aliados,
        },
        { emitEvent: false }
      );
    }

    group.markAsDirty();
    group.updateValueAndValidity();
    this.syncingTipos = false;
  }

  onMontoBlur(): void {
    const ctrl = this.form.get('monto');
    if (!ctrl) {
      return;
    }
    const digits = String(ctrl.value ?? '').replace(/\D/g, '');
    if (!digits) {
      ctrl.setValue('');
      this.validarMonto();
      this.aplicarDestinosPorSegmento();
      return;
    }
    const formatted = Number(digits).toLocaleString('es-CO');
    ctrl.setValue(`$${formatted}`);
    this.validarMonto();
    this.validarPlazo();
    this.aplicarDestinosPorSegmento();
  }

  onMontoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    if (!digits) {
      this.form.get('monto')?.setValue('', { emitEvent: true });
      this.validarMonto();
      this.aplicarDestinosPorSegmento();
      return;
    }
    const formatted = `$${Number(digits).toLocaleString('es-CO')}`;
    this.form.get('monto')?.setValue(formatted, { emitEvent: true });
    this.validarMonto();
    this.validarPlazo();
    this.aplicarDestinosPorSegmento();
  }

  onPlazoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 3);
    this.form.get('plazo')?.setValue(digits, { emitEvent: true });
    this.validarPlazo();
    this.aplicarDestinosPorSegmento();
  }

  /**
   * El input tiene pointer-events:none; el click llega al label.
   * Forzamos el valor en el FormControl para evitar desfase visual vs modelo
   * al reconstruir el catálogo express/natural/comunes.
   */
  onDestinoToggle(id: string, event: Event): void {
    event.preventDefault();
    const group = this.form.get('destinos') as UntypedFormGroup;
    const ctrl = group.get(id);
    if (!ctrl) {
      return;
    }
    ctrl.setValue(!ctrl.value);
    ctrl.markAsDirty();
    group.markAsDirty();
    this.form.markAsDirty();
    this.syncOtroTextoValidators();
    this.form.updateValueAndValidity();
  }

  onContinuar(): void {
    this.validarMonto();
    this.validarPlazo();
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const destinosMarcados = this.destinos.filter(
      (d) => value.destinos?.[d.id]
    );
    const destinosSeleccionados = destinosMarcados.map((d) => d.id);
    const idsDestinoCredito = destinosMarcados
      .map((d) => d.idDestinoCredito)
      .filter((id): id is number => id != null && Number.isFinite(Number(id)))
      .map((id) => Number(id));
    const destinosLabels = destinosMarcados.map((d) => {
      if (d.esOtro && value.otroTexto) {
        return `${d.label}: ${value.otroTexto}`;
      }
      return d.label;
    });
    const requiereTexto = this.destinos.some(
      (d) => d.esOtro && value.destinos?.[d.id]
    );
    const tipoLinea = resolveTipoLinea(value.tipoCredito);
    this.segmentoService.applyFromMontoPlazo(value.monto, value.plazo, {
      tipoCredito: value.tipoCredito,
      destinos: destinosSeleccionados,
      idsDestinoCredito,
      tipoLinea,
      otroTexto: requiereTexto ? value.otroTexto : null,
    });

    this.saving = true;
    this.sub.add(
      this.solicitudService
        .crearDesdeSolicitaCredito({
          tipoCredito: value.tipoCredito,
          destinos: destinosSeleccionados,
          idsDestinoCredito,
          tipoLinea,
          otroTexto: requiereTexto ? value.otroTexto : null,
          destinosLabels,
        })
        .subscribe((res) => {
          this.saving = false;
          if (!res.exitoso) {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              res.error || 'No se pudo crear la solicitud'
            );
            return;
          }
          // PN y PJ: siempre hub perfil (PJ sin rama Express).
          void this.router.navigateByUrl('/microempresario/perfil');
        })
    );
  }

  /**
   * Precarga catálogos al entrar.
   * PN: express + natural en paralelo (una sola vez). El cambio monto/plazo
   * solo intercambia la lista en cliente, sin nuevas llamadas al API.
   */
  private precargarDestinos(): void {
    if (isPerfilJuridica(this.userService.getUser()?.tipoPersona)) {
      this.sub.add(
        this.parametrosService
          .getDestinosCreditoPerfil('juridica')
          .pipe(catchError(() => of([])))
          .subscribe((res) => {
            this.applyDestinos(
              this.mapDestinosApi(this.extraerListaDestinos(res))
            );
            this.lastTipoDestinos = 'juridica';
            this.destinosReady = true;
          })
      );
      return;
    }

    this.sub.add(
      forkJoin({
        express: this.parametrosService
          .getDestinosCreditoPerfil('express')
          .pipe(catchError(() => of([]))),
        natural: this.parametrosService
          .getDestinosCreditoPerfil('natural')
          .pipe(catchError(() => of([]))),
      }).subscribe(({ express, natural }) => {
        this.destinosExpress = this.mapDestinosApi(
          this.extraerListaDestinos(express)
        );
        this.destinosNatural = this.mapDestinosApi(
          this.extraerListaDestinos(natural)
        );
        this.destinosComunes = this.interseccionDestinos(
          this.destinosExpress,
          this.destinosNatural
        );
        this.destinosReady = true;
        this.aplicarDestinosPorSegmento();
      })
    );
  }

  /**
   * Elige el catálogo precargado según segmento (PAR-068/069/071).
   * Sin monto/plazo → comunes (express ∩ natural), para no mostrar
   * destinos exclusivos de un tipo antes de conocer el segmento.
   */
  private aplicarDestinosPorSegmento(): void {
    if (isPerfilJuridica(this.userService.getUser()?.tipoPersona)) {
      return;
    }
    if (!this.destinosReady) {
      return;
    }
    const tipo = this.resolverTipoDestinosPn();
    if (tipo === this.lastTipoDestinos) {
      return;
    }
    const list =
      tipo === 'express'
        ? this.destinosExpress
        : tipo === 'natural'
          ? this.destinosNatural
          : this.destinosComunes;
    this.applyDestinos(list);
    this.lastTipoDestinos = tipo;
  }

  private resolverTipoDestinosPn(): 'express' | 'natural' | 'comunes' {
    if (!this.paramsReady) {
      return 'comunes';
    }
    const monto = parseMontoDigits(this.form.get('monto')?.value);
    const plazo = parsePlazoNumber(this.form.get('plazo')?.value);
    if (!monto || !plazo) {
      return 'comunes';
    }
    return segmentoToTipoPersonaApi(
      resolveSegmento(monto, plazo, this.segmentoService.paramsSnapshot)
    );
  }

  /** Destinos presentes en ambos catálogos (mismo id). */
  private interseccionDestinos(
    express: SolicitaCreditoDestino[],
    natural: SolicitaCreditoDestino[]
  ): SolicitaCreditoDestino[] {
    const naturalIds = new Set(natural.map((d) => d.id));
    return express
      .filter((d) => naturalIds.has(d.id))
      .map((d, index) => ({
        ...d,
        columna: (index % 2 === 0 ? 1 : 2) as 1 | 2,
      }));
  }

  private extraerListaDestinos(res: unknown): DestinoCreditoApi[] {
    if (Array.isArray(res)) {
      return res as DestinoCreditoApi[];
    }
    if (
      res &&
      typeof res === 'object' &&
      Array.isArray((res as { data?: unknown }).data)
    ) {
      return (res as { data: DestinoCreditoApi[] }).data;
    }
    return [];
  }

  private mapDestinosApi(rows: DestinoCreditoApi[]): SolicitaCreditoDestino[] {
    const byKey = new Map<string, SolicitaCreditoDestino>();
    const sorted = [...rows].sort(
      (a, b) => (a.orden ?? 0) - (b.orden ?? 0)
    );
    let colIndex = 0;

    for (const row of sorted) {
      const id =
        row.codigo ||
        (row.idDestinoCredito != null
          ? String(row.idDestinoCredito)
          : `destino_${colIndex}`);
      if (byKey.has(id)) {
        continue;
      }
      colIndex += 1;
      byKey.set(id, {
        id,
        label: row.nombre || id,
        columna: colIndex % 2 === 1 ? 1 : 2,
        idDestinoCredito: row.idDestinoCredito,
        esOtro: this.isEsOtroApi(row),
        tooltip: (row.tooltip || '').trim() || undefined,
      });
    }

    return [...byKey.values()];
  }

  private isEsOtroApi(row: DestinoCreditoApi): boolean {
    if (row.esOtro === true || row.esOtro === 1) {
      return true;
    }
    if (typeof row.esOtro === 'string' && row.esOtro === '1') {
      return true;
    }
    return false;
  }

  private applyDestinos(list: SolicitaCreditoDestino[]): void {
    const group = this.form.get('destinos') as UntypedFormGroup;
    const nextIds = new Set(list.map((d) => d.id));

    // Conservar controles existentes (y su valor) para no desincronizar el DOM.
    for (const key of Object.keys(group.controls)) {
      if (!nextIds.has(key)) {
        group.removeControl(key, { emitEvent: false });
      }
    }
    for (const d of list) {
      if (!group.contains(d.id)) {
        group.addControl(d.id, this.fb.control(false), { emitEvent: false });
      }
    }

    this.destinos = list;
    this.syncOtroTextoValidators();
    group.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  /** Misma lógica que SolicitudNaturalForm.validarMonto. */
  private validarMonto(): void {
    const ctrl = this.form.get('monto');
    if (!ctrl) {
      return;
    }
    const params = this.segmentoService.paramsSnapshot;
    const raw = ctrl.value;
    if (!raw) {
      this.montoErrorMessage = this.copy.validaciones.campoObligatorio;
      ctrl.setErrors({ required: true });
      return;
    }
    const auxMonto = parseMontoDigits(raw);
    const minLabel = params.montoMinExpress.toLocaleString('es-CO');
    const maxLabel = params.montoMaximoGlobal.toLocaleString('es-CO');

    if (auxMonto > params.montoMaximoGlobal) {
      this.montoErrorMessage = `Campo obligatorio. El monto máximo es de $${maxLabel} pesos.`;
      ctrl.setErrors({ incorrect: true });
      return;
    }
    if (auxMonto < params.montoMinExpress) {
      this.montoErrorMessage = `Campo obligatorio. El monto mínimo es de $${minLabel} pesos.`;
      ctrl.setErrors({ incorrect: true });
      return;
    }
    this.montoErrorMessage = '';
    if (ctrl.hasError('incorrect')) {
      ctrl.setErrors(null);
      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** Misma lógica que SolicitudNaturalForm.validarPlazo. */
  private validarPlazo(): void {
    const ctrl = this.form.get('plazo');
    if (!ctrl) {
      return;
    }
    const params = this.segmentoService.paramsSnapshot;
    const raw = ctrl.value;
    if (!raw) {
      this.plazoErrorMessage = this.copy.validaciones.campoObligatorio;
      ctrl.setErrors({ required: true });
      return;
    }
    const plazo = parsePlazoNumber(raw);
    if (plazo < params.plazoMinExpress) {
      this.plazoErrorMessage = `El plazo mínimo permitido es de ${params.plazoMinExpress} meses.`;
      ctrl.setErrors({ incorrect: true });
      return;
    }
    this.plazoErrorMessage = '';
    if (ctrl.hasError('incorrect') || ctrl.hasError('pattern')) {
      const nextErrors: ValidationErrors | null = ctrl.hasError('pattern')
        ? { pattern: true }
        : null;
      ctrl.setErrors(nextErrors);
      if (!nextErrors) {
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }
}

function tipoCreditoRequeridoValidator(
  control: AbstractControl
): ValidationErrors | null {
  const v = control.value as
    | { bancoldex?: boolean; aliados?: boolean; ambas?: boolean }
    | null;
  if (v?.bancoldex || v?.aliados || v?.ambas) {
    return null;
  }
  return { required: true };
}

function destinosRequeridosValidator(
  group: AbstractControl
): ValidationErrors | null {
  const destinos = group.get('destinos')?.value as
    | Record<string, boolean>
    | undefined;
  const hasDestino = Object.values(destinos ?? {}).some((v) => !!v);
  return hasDestino ? null : { destinosRequeridos: true };
}
